/*
 * jsx-a11y ne reconnaît pas `<dialog>` comme un élément interactif et réclame
 * un écouteur clavier sur le gestionnaire de clic du fond. Il n'y a rien à y
 * mettre : Échap est géré par le navigateur, et deux boutons « Fermer »
 * encadrent le contenu — un en haut, un en bas. Le clic sur le fond n'est qu'un
 * confort à la souris.
 */
// oxlint-disable jsx-a11y/click-events-have-key-events
// oxlint-disable jsx-a11y/no-noninteractive-element-interactions
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useI18n } from "../i18n/index.ts";
import { SITE_IDENTITY } from "../legal/site-identity.ts";

/**
 * L'adresse de la page est un fragment plutôt qu'une route : elle reste
 * partageable et gère les boutons précédent/suivant, sans imposer un routeur à
 * une application qui n'a par ailleurs qu'un seul écran.
 */
const HASH = "#vie-privee";

const isOpenFromHash = (): boolean => globalThis.location?.hash === HASH;

/**
 * Un vrai `<dialog>` plutôt qu'un `div role="dialog"` : le navigateur piège le
 * focus, gère Échap et dessine le fond (`::backdrop`). Trois comportements
 * d'accessibilité qu'il aurait fallu réécrire à la main, moins bien.
 */
export const LegalNotice = (): React.ReactElement => {
  const { t } = useI18n();
  const [open, setOpen] = useState(isOpenFromHash);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const sync = (): void => setOpen(isOpenFromHash());

    globalThis.addEventListener("hashchange", sync);

    return () => globalThis.removeEventListener("hashchange", sync);
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;

    if (dialog === null) {
      return;
    }

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const close = useCallback(() => {
    // Retirer le fragment sans ajouter d'entrée d'historique : sortir de la page
    // ne doit pas obliger à appuyer deux fois sur « précédent ».
    globalThis.history.replaceState(null, "", globalThis.location.pathname);
    setOpen(false);
  }, []);

  return (
    <>
      <a className="legal-link" href={HASH} onClick={() => setOpen(true)}>
        {t.legal.link}
      </a>

      {/* Rendu dans `body` et non là où le composant est monté. Le dialogue
          vivait dans le `<header>`, où une règle `.topbar p { display: none }`
          destinée à masquer la baseline sur mobile masquait aussi **tout le
          texte des mentions légales** : seuls les titres s'affichaient. Un
          modal n'a rien à faire sous un ancêtre qui porte ses propres règles. */}
      {createPortal(
        <dialog
          ref={dialogRef}
          className="legal"
          aria-labelledby="legal-title"
          onClose={close}
          // Un clic sur le fond a pour cible l'élément `dialog` lui-même ; le
          // contenu, qui le remplit entièrement, intercepte les autres.
          onClick={(event) => {
            if (event.target === dialogRef.current) {
              close();
            }
          }}
        >
          <div className="legal__body">
            <header className="legal__header">
              <h2 id="legal-title">{t.legal.title}</h2>
              <button
                type="button"
                className="sheet__close"
                onClick={close}
                aria-label={t.legal.close}
              >
                ✕
              </button>
            </header>

            {t.legal.sections.map((section) => (
              <section key={section.heading} className="legal__section">
                <h3>{section.heading}</h3>
                <p>{section.body}</p>
              </section>
            ))}

            <section className="legal__section">
              <h3>{t.legal.identity.heading}</h3>
              <p>{t.legal.identity.publisher}</p>
              <p>
                <strong>{t.legal.identity.contactLabel}</strong> —{" "}
                <a href={`mailto:${SITE_IDENTITY.contactEmail}`}>{SITE_IDENTITY.contactEmail}</a>
              </p>
            </section>

            <p className="legal__updated">{t.legal.updated(SITE_IDENTITY.lastUpdated)}</p>

            <div className="sheet__actions">
              <button type="button" className="button button--primary" onClick={close}>
                {t.legal.close}
              </button>
            </div>
          </div>
        </dialog>,
        document.body,
      )}
    </>
  );
};
